// ---------------------------------------------------------------------------
// Aesthetic directions — the "trim level" axis of the theme system.
// Color presets (default/midnight/ember/forest/clay) are the "color" axis.
// Aesthetics define: neutral surfaces, borders, semantic status colors,
// shadow base, typography stack, and dark mode surfaces.
// ---------------------------------------------------------------------------

export type AestheticId = 'classic' | 'clubhouse';

export interface Aesthetic {
	id: AestheticId;
	/** i18n key suffix — used as translate(`appearance.aesthetic_${nameKey}`) */
	nameKey: string;
	/** Neutral surface colors (light mode) */
	surfaces: {
		pageBg: string;
		cardBg: string;
		border: string;
		borderSubtle: string;
		textPrimary: string;
		textSecondary: string;
		textMuted: string;
	};
	/** Dark mode surface overrides for navy 700/800/900 */
	darkSurfaces: {
		'700': string;
		'800': string;
		'900': string;
	};
	/** Dark mode border */
	darkBorder: string;
	/** Semantic status colors */
	semantic: {
		warning: { '50': string; '100': string; '500': string; '600': string };
		danger: { '50': string; '100': string; '500': string; '600': string };
		info: { '50': string; '100': string; '500': string; '600': string };
	};
	/** Shadow rgba base (for non-brand shadows) */
	shadowBase: string;
	/** Extra Google Fonts families to load (appended to base Figtree+Urbanist) */
	extraFonts: string[];
	/** Whether to use Instrument Serif for display text */
	useDisplaySerif: boolean;
	/** Whether to use JetBrains Mono for data display */
	useMonoData: boolean;
}

// ---------------------------------------------------------------------------
// Classic — current cool-gray aesthetic (unchanged from existing theme)
// ---------------------------------------------------------------------------
const classicAesthetic: Aesthetic = {
	id: 'classic',
	nameKey: 'classic',
	surfaces: {
		pageBg: '#F4F5F7',
		cardBg: '#FFFFFF',
		border: '#E5E7EB',
		borderSubtle: '#EBEDF0',
		textPrimary: '#1A1D26',
		textSecondary: '#6B7280',
		textMuted: '#9CA3AF',
	},
	darkSurfaces: {
		'700': '#27272A',
		'800': '#18181B',
		'900': '#09090B',
	},
	darkBorder: 'rgba(255, 255, 255, 0.06)',
	semantic: {
		warning: { '50': '#FFFBEB', '100': '#FEF3C7', '500': '#F59E0B', '600': '#D97706' },
		danger: { '50': '#FEF2F2', '100': '#FEE2E2', '500': '#EF4444', '600': '#DC2626' },
		info: { '50': '#EBF5FF', '100': '#D6EAFF', '500': '#3B82F6', '600': '#2563EB' },
	},
	shadowBase: 'rgba(16, 24, 40,',
	extraFonts: [],
	useDisplaySerif: false,
	useMonoData: false,
};

// ---------------------------------------------------------------------------
// Clubhouse — warm neutral aesthetic ("The Clubhouse" direction)
// ---------------------------------------------------------------------------
const clubhouseAesthetic: Aesthetic = {
	id: 'clubhouse',
	nameKey: 'clubhouse',
	surfaces: {
		pageBg: '#F7F6F3',
		cardBg: '#FFFFFF',
		border: '#E5E2DC',
		borderSubtle: '#EDEAE4',
		textPrimary: '#2C2A26',
		textSecondary: '#6B6860',
		textMuted: '#9C9889',
	},
	darkSurfaces: {
		'700': '#2C2B28',
		'800': '#242320',
		'900': '#1C1B18',
	},
	darkBorder: 'rgba(255, 255, 255, 0.08)',
	semantic: {
		warning: { '50': '#FEF8F0', '100': '#FDF0D5', '500': '#C4883A', '600': '#A06E2C' },
		danger: { '50': '#FEF2F0', '100': '#FDE2DD', '500': '#B8453A', '600': '#96362C' },
		info: { '50': '#EFF4F8', '100': '#DCEAF4', '500': '#5B7B9A', '600': '#4A6580' },
	},
	shadowBase: 'rgba(44, 42, 38,',
	extraFonts: [
		'Instrument+Serif',
		'JetBrains+Mono:wght@400;500',
	],
	useDisplaySerif: true,
	useMonoData: true,
};

// ---------------------------------------------------------------------------
// Export
// ---------------------------------------------------------------------------
export const aesthetics: Record<AestheticId, Aesthetic> = {
	classic: classicAesthetic,
	clubhouse: clubhouseAesthetic,
};

export const aestheticIds = Object.keys(aesthetics) as AestheticId[];
